/** Grain's canonical tooltip — a light/dark speech-bubble with an SVG triangle tip, built on Radix Tooltip. */

import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '@/lib/utils';

const TooltipProvider = TooltipPrimitive.Provider;
const Tooltip = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;

type TooltipVariant = 'light' | 'dark';

const tooltipBubbleBase = [
  'max-w-[230px] rounded-[10px]',
  'border-[0.5px] px-[18px] py-[12px] font-inter text-[15px] font-normal leading-tight',
  'shadow-soft',
] as const;

const tooltipBubbleVariants: Record<TooltipVariant, string> = {
  light: 'border-dinnerplate-white bg-body text-off-black',
  dark: 'border-off-black bg-off-black text-dinnerplate-white font-normal',
};

const tooltipArrowVariants: Record<TooltipVariant, { className: string; width: number; height: number; path: string }> = {
  light: {
    className: 'fill-body stroke-dinnerplate-white',
    width: 20.94,
    height: 10.6,
    path: 'M 0 0 C 0.3 0 0.58 0.13 0.77 0.36 L 8.55 9.7 C 9.55 10.89 11.39 10.89 12.39 9.7 L 20.17 0.36 C 20.36 0.13 20.64 0 20.94 0',
  },
  dark: {
    className: 'fill-off-black stroke-off-black',
    width: 15,
    height: 9,
    path: 'M 0 0 L 6.35 7.62 A 1.5 1.5 0 0 0 8.65 7.62 L 15 0',
  },
};

// Overlaps the bubble by its border width so the outline has no seam at the tip.
function TooltipArrow({ variant }: { variant: TooltipVariant }) {
  const { className, width, height, path } = tooltipArrowVariants[variant];
  return (
    <TooltipPrimitive.Arrow asChild width={width} height={height} style={{ overflow: 'visible' }}>
      <div className="relative">
        <svg
          aria-hidden="true"
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          className={cn('absolute left-1/2 top-[-0.5px] block -translate-x-1/2 overflow-visible', className)}
        >
          <path d={`${path} Z`} stroke="none" />
          <path d={path} fill="none" strokeWidth={0.5} />
        </svg>
      </div>
    </TooltipPrimitive.Arrow>
  );
}

// Lives on the outer Content (not the bubble): Radix sets data-state there, and
// data-[state=closed]:* exit selectors don't cascade to children.
const tooltipAnimationStyles = [
  'animate-in fade-in-0 zoom-in-95',
  'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
  'data-[side=bottom]:slide-in-from-top-2',
  'data-[side=left]:slide-in-from-right-2',
  'data-[side=right]:slide-in-from-left-2',
  'data-[side=top]:slide-in-from-bottom-2',
] as const;

interface TooltipContentProps extends React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> {
  variant?: TooltipVariant;
  /** Lets clicks through to whatever the bubble covers (see application.css). */
  passthrough?: boolean;
}

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  TooltipContentProps
>(({ className, sideOffset = 9, side = 'top', variant = 'light', passthrough, children, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      side={side}
      sideOffset={sideOffset}
      className={cn('z-[70]', tooltipAnimationStyles)}
      data-pointer-passthrough={passthrough || undefined}
      {...props}
    >
      <div className={cn(tooltipBubbleBase, tooltipBubbleVariants[variant], className)}>{children}</div>
      <TooltipArrow variant={variant} />
    </TooltipPrimitive.Content>
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
